import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AppointmentRequestRow, AvailabilitySlot } from '@his/shared';
import { get, put, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { visibleInterval } from '@/lib/polling';
import { dateTime, humanize, timeOnly, todayIso } from '@/lib/utils';
import { DataTable, Dialog, PageHeader, Tabs } from '@/components/ui/layout';
import { Alert, Badge, Button, DatePicker, Field, Select, StatusBadge, Textarea } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';

/**
 * Requests sent in from the public directory.
 *
 * Approving is the same decision the front desk makes at the counter, so it
 * runs through the same booking rules: the patient is matched or registered,
 * and the slot is taken through the ordinary diary. A request that cannot be
 * honoured is rejected with a reason the patient actually sees.
 */
export function RequestsInboxPage() {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [status, setStatus] = useState('pending');
  const [approving, setApproving] = useState<AppointmentRequestRow | null>(null);
  const [rejecting, setRejecting] = useState<AppointmentRequestRow | null>(null);

  const manage = can('appointment.manage');

  const requests = useQuery({
    queryKey: ['directory-requests', status],
    queryFn: () => get<AppointmentRequestRow[]>(`/directory/requests${qs({ status: status || undefined })}`),
    refetchInterval: visibleInterval(120_000),
  });

  const pendingCount = requests.data?.filter((row) => row.status === 'pending').length ?? 0;

  return (
    <>
      <PageHeader
        title="Online requests"
        description="Appointments and lab visits asked for from the public directory"
      />

      <Tabs
        tabs={[
          {
            id: 'pending',
            label: 'Waiting',
            badge: status === 'pending' && pendingCount > 0 ? <Badge variant="warning">{pendingCount}</Badge> : undefined,
          },
          { id: 'approved', label: 'Approved' },
          { id: 'rejected', label: 'Rejected' },
          { id: '', label: 'All' },
        ]}
        active={status}
        onChange={setStatus}
      />

      <DataTable
        rows={requests.data}
        loading={requests.isLoading} refreshing={requests.isFetching}
        error={requests.error}
        rowKey={(row) => row.id}
        empty="Nothing here. Requests sent from the public site land in this list."
        columns={[
          {
            header: 'Patient',
            sort: (row) => row.patientName,
            cell: (row) => (
              <div>
                <p className="font-medium">{row.patientName}</p>
                <p className="text-xs text-muted-foreground">
                  {row.phone}
                  {row.sex ? ` · ${humanize(row.sex, 'sex')}` : ''}
                </p>
              </div>
            ),
          },
          {
            header: 'Asking for',
            cell: (row) => (
              <div>
                <Badge variant="secondary">{row.kind === 'lab' ? 'Lab tests' : 'Doctor visit'}</Badge>
                <p className="mt-1 text-xs text-muted-foreground">
                  {row.kind === 'lab'
                    ? row.requestedTests.map((test) => test.name).join(', ') || '—'
                    : (row.doctorName ?? 'Any doctor')}
                </p>
              </div>
            ),
          },
          { header: 'Branch', sort: (row) => row.branchName, cell: (row) => row.branchName },
          {
            header: 'Preferred time',
            sort: (row) => row.requestedSlotAt,
            cell: (row) =>
              row.requestedSlotAt ? (
                <span className="text-sm">{dateTime(row.requestedSlotAt)}</span>
              ) : (
                <span className="text-muted-foreground">No preference</span>
              ),
          },
          { header: 'Sent', sort: (row) => row.createdAt, cell: (row) => <span className="text-sm">{dateTime(row.createdAt)}</span> },
          { header: 'Status', sort: (row) => row.status, cell: (row) => <StatusBadge status={row.status} /> },
          {
            header: '',
            align: 'right',
            cell: (row) =>
              manage && row.status === 'pending' ? (
                <div className="flex justify-end gap-1">
                  <Button size="sm" variant="outline" onClick={() => setRejecting(row)}>
                    Reject
                  </Button>
                  <Button size="sm" onClick={() => setApproving(row)}>
                    Approve
                  </Button>
                </div>
              ) : row.status === 'rejected' && row.rejectionReason ? (
                <span className="text-xs text-muted-foreground">{row.rejectionReason}</span>
              ) : null,
          },
        ]}
      />

      {approving && (
        <ApproveDialog
          request={approving}
          onClose={() => setApproving(null)}
          onDone={() => {
            setApproving(null);
            toast.success('Request approved', 'The patient is registered and the visit is booked.');
            void queryClient.invalidateQueries({ queryKey: ['directory-requests'] });
            void queryClient.invalidateQueries({ queryKey: ['appointments'] });
            void queryClient.invalidateQueries({ queryKey: ['queue'] });
          }}
        />
      )}

      {rejecting && (
        <RejectDialog
          request={rejecting}
          onClose={() => setRejecting(null)}
          onDone={() => {
            setRejecting(null);
            void queryClient.invalidateQueries({ queryKey: ['directory-requests'] });
          }}
        />
      )}
    </>
  );
}

function ApproveDialog({
  request,
  onClose,
  onDone,
}: {
  request: AppointmentRequestRow;
  onClose: () => void;
  onDone: () => void;
}) {
  const isLab = request.kind === 'lab';
  const preferred = request.requestedSlotAt ? request.requestedSlotAt.slice(0, 10) : todayIso();

  const [doctorId, setDoctorId] = useState(request.doctorId ?? '');
  const [date, setDate] = useState(preferred);
  const [slotAt, setSlotAt] = useState(request.requestedSlotAt ?? '');
  const [error, setError] = useState<string | null>(null);

  const doctors = useQuery({
    queryKey: ['bookable-doctors'],
    queryFn: async () =>
      (await get<{ data: { id: string; name: string }[] }>('/org/clinicians?kind=bookable&limit=200')).data,
    enabled: !isLab,
  });

  const availability = useQuery({
    queryKey: ['availability', doctorId, date],
    queryFn: () => get<AvailabilitySlot[]>(`/schedules/availability${qs({ doctorId, date })}`),
    enabled: !isLab && Boolean(doctorId && date),
  });

  const approve = useMutation({
    mutationFn: () =>
      put(`/directory/requests/${request.id}/approve`, isLab ? {} : { doctorId, slotAt }),
    onSuccess: onDone,
    onError: (caught) => setError(describeError(caught)),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={isLab ? 'Approve this lab visit' : 'Approve and book this visit'}
      description={
        isLab
          ? 'The patient is registered and the tests are ordered and billed'
          : 'The patient is registered if new, then the slot is booked'
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              setError(null);
              approve.mutate();
            }}
            loading={approve.isPending}
            disabled={!isLab && (!doctorId || !slotAt)}
          >
            Approve
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && <Alert variant="destructive">{error}</Alert>}

        <div className="rounded-md border p-3 text-sm">
          <p className="font-medium">{request.patientName}</p>
          <p className="text-xs text-muted-foreground">{request.phone}</p>
          {request.notes && <p className="mt-2 text-muted-foreground">{request.notes}</p>}
          {isLab && (
            <ul className="mt-2 list-inside list-disc text-muted-foreground">
              {request.requestedTests.map((test) => (
                <li key={test.id}>{test.name}</li>
              ))}
            </ul>
          )}
        </div>

        {!isLab && (
          <>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Doctor" required>
                <Select
                  value={doctorId}
                  onChange={(e) => {
                    setDoctorId(e.target.value);
                    setSlotAt('');
                  }}
                >
                  <option value="">Select a doctor</option>
                  {doctors.data?.map((doctor) => (
                    <option key={doctor.id} value={doctor.id}>
                      {doctor.name}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Date" required>
                <DatePicker
                  value={date}
                  onChange={(value) => {
                    setDate(value);
                    setSlotAt('');
                  }}
                />
              </Field>
            </div>

            <Field
              label="Slot"
              required
              hint={
                request.requestedSlotAt
                  ? `The patient asked for ${dateTime(request.requestedSlotAt)}`
                  : 'The patient did not ask for a particular time'
              }
            >
              <div className="grid max-h-40 grid-cols-3 gap-1.5 overflow-y-auto rounded-md border p-2 sm:grid-cols-4">
                {availability.data?.map((slot) => (
                  <button
                    key={slot.startsAt}
                    type="button"
                    disabled={!slot.available && slot.startsAt !== slotAt}
                    onClick={() => setSlotAt(slot.startsAt)}
                    className={`rounded px-2 py-1 text-xs tabular transition-colors focus-ring ${
                      slotAt === slot.startsAt
                        ? 'bg-primary text-primary-foreground'
                        : slot.available
                          ? 'bg-muted hover:bg-accent'
                          : 'cursor-not-allowed bg-muted/40 text-muted-foreground line-through'
                    }`}
                  >
                    {timeOnly(slot.startsAt)}
                  </button>
                ))}
                {availability.data?.length === 0 && (
                  <p className="col-span-full py-3 text-center text-sm text-muted-foreground">
                    No clinic on this day.
                  </p>
                )}
              </div>
            </Field>
          </>
        )}
      </div>
    </Dialog>
  );
}

function RejectDialog({
  request,
  onClose,
  onDone,
}: {
  request: AppointmentRequestRow;
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const reject = useMutation({
    mutationFn: () => put(`/directory/requests/${request.id}/reject`, { reason }),
    onSuccess: () => {
      toast.success('Request rejected', 'The patient can see the reason you gave.');
      onDone();
    },
    onError: (caught) => setError(describeError(caught)),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title="Reject this request"
      description="The patient sees this reason, so keep it plain and polite"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => {
              setError(null);
              reject.mutate();
            }}
            loading={reject.isPending}
            disabled={reason.trim().length < 3}
          >
            Reject
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && <Alert variant="destructive">{error}</Alert>}
        <Field label="Reason" required>
          <Textarea
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="The doctor is on leave that week — please pick another date."
          />
        </Field>
      </div>
    </Dialog>
  );
}
